import { refreshWaiting } from './waiting'
import { dateLocaleFor, type HubLang } from './i18n'
import { SHOP } from './hubCopy'
import { supabase } from './supabase'
import type { Me } from './me'
import { can } from './me'

/**
 * The shop — rooms, items, variants, prices and stock. REC-10, REC-9, REC-24.
 *
 * ── TWO READS, AND THEY ARE NOT INTERCHANGEABLE ─────────────────────────────
 *
 * `shop_catalogue()` is what a SHOPPER sees: active items only, and `free`
 * alone, because free is the only number somebody deciding whether to order can
 * act on. `shop_manage_items()` is what a MANAGER sees: every item including
 * delisted ones, and all three numbers per room.
 *
 * Do not reach for the manager's read to render the shop floor "because it has
 * more". It carries delisted stock and the audience rows, and putting those in
 * front of ~900 people is a different screen with a different gate.
 *
 * ── free IS NEVER STORED AND IS NEVER RECOMPUTED HERE ───────────────────────
 *
 * on_hand and held are columns; free is on_hand - held, computed in the
 * database at every level it is shown. A second definition in TypeScript is a
 * third number that can be wrong while both its inputs are right — 065's
 * argument, and the reason it is not a column either.
 *
 * ── AND DO NOT FILTER BY ROOM IN THIS FILE ──────────────────────────────────
 *
 * `p_room` goes to the function. The counts are SUMMED server-side, so
 * narrowing a fetched result in the browser does not produce a rougher right
 * answer — it produces the total for every room with one room's label on it.
 * Sixth time this is written down (039, 044, 046, 054, 062).
 *
 * ── THERE IS NO PRICE HISTORY, AND THAT IS STATED RATHER THAN IMPLIED ───────
 *
 * set_shop_item_price overwrites. An order records what it charged (REC-11), so
 * history lives on the order and re-pricing never rewrites what somebody
 * already paid.
 */

export type StockRoom = {
  id: string
  code: string
  name: string
  street: string
  city: string
  region: string | null
  country: string
  /**
   * 071. Nullable because some yards carry no postcode on any record we hold
   * (Grande Prairie) — a required field there is a rule that makes the truthful
   * row impossible, so somebody types a plausible one instead, which is worse
   * than a blank because it looks answered.
   */
  postalCode: string | null
  timezone: string
  isActive: boolean
  /** Per row, so one screen can list every room and offer controls on yours. */
  iManage: boolean
}

export type RoomManager = {
  stockRoomId: string
  roomName: string
  personId: string
  displayName: string
  addedAt: string
}

export type VariantStockRow = {
  stockRoomId: string
  roomName: string
  roomCity: string
  onHand: number
  held: number
  free: number
}

/**
 * AN ITEM VARIES BY ANY NUMBER OF AXES (068).
 *
 * `kind` is what 067's fixed columns earned and a naive axis model would throw
 * away: `size` orders by rank rather than alphabetically, and a report can find
 * every size option across the catalogue. `custom` is whatever the item needs.
 *
 * **`fit` IS A GARMENT CUT AND NEVER A PERSON'S GENDER** (David, 14 Sep). The
 * shirt comes in a men's cut and a women's cut and what is recorded is which
 * SHIRT — anybody may want either, nothing about the person is stored, and a
 * t-shirt never becomes a reason to hold personal data with France in EU GDPR
 * scope. Do not relabel these options Male/Female.
 */
export type AxisKind = 'size' | 'colour' | 'fit' | 'custom'

/**
 * A picture, and WHERE IT CAME FROM (070).
 *
 * `source` is not decoration. There are three levels — the item default, a
 * picture on an OPTION (one per colour; none per size, because size does not
 * change what the thing looks like), and a per-combination override — and the
 * manager needs to know which one they are looking at before they can change
 * it. A photograph with no provenance is one whose upload control cannot be
 * found.
 *
 * The RANKING lives in `app.shop_image_for` and nowhere else. Do not re-derive
 * it here: the tie-break when two axes are photographed is the axis POSITION,
 * which the client would have to reimplement and would get wrong the first time
 * somebody reorders (REC-29).
 */
export type ShopImage = {
  path: string
  /** Both NOT NULL beside the path, so a card reserves its box (019). */
  width: number
  height: number
  source: 'item' | 'option' | 'variant'
  /** Present only on an option image — "from the Navy option". */
  optionId?: string | null
  optionLabel?: string | null
}

function readImage(raw: unknown): ShopImage | null {
  if (!raw || typeof raw !== 'object') return null
  const o = raw as Record<string, unknown>
  if (!o.path) return null
  return {
    path: o.path as string,
    width: (o.width as number) ?? 0,
    height: (o.height as number) ?? 0,
    source: (o.source as ShopImage['source']) ?? 'item',
    optionId: (o.option_id as string) ?? null,
    optionLabel: (o.option_label as string) ?? null,
  }
}

export type AxisOption = {
  optionId: string
  label: string
  /** Null on most options, and that is the normal case — see ShopImage. */
  imagePath: string | null
  imageWidth: number | null
  imageHeight: number | null
}

export type ItemAxis = {
  axisId: string
  kind: AxisKind
  label: string
  position: number
  options: AxisOption[]
}

/**
 * A variant is a COMBINATION — a set of one option per axis.
 *
 * `label` is the rendered combination ("Navy · M · Men's") and comes from
 * `app.variant_label`, so the admin grid and the shop floor cannot name the
 * same shirt differently. Do not rebuild it here from the parts; that is a
 * second definition and the first thing it will disagree about is axis order.
 */
export type ManagedVariant = {
  variantId: string
  label: string
  sortOrder: number
  isActive: boolean
  /**
   * BOTH, and they are different questions. `ownImage` is the override this
   * combination carries itself and is what the clear control acts on; `image`
   * is what actually renders, inherited or not. One alone leaves the manager
   * unable to tell an override from a picture coming from the colour.
   */
  ownImage: { path: string; width: number; height: number } | null
  image: ShopImage | null
  onHand: number
  held: number
  free: number
  rooms: VariantStockRow[]
}

export type AudienceRow = {
  eligibilityId: string
  orgUnitId: string | null
  personId: string | null
  /** Null when RLS hides the target. The row is still listed — see below. */
  label: string | null
}

export type ManagedItem = {
  itemId: string
  code: string
  name: string
  description: string | null
  /** What this item varies by, in render order. Empty = it comes one way. */
  axes: ItemAxis[]
  priceStars: number
  ownerRoomId: string | null
  ownerRoomName: string | null
  /**
   * 069's ordering rules. The columns and their write path landed with the
   * order queue; the manager's READ never returned them, so nothing on screen
   * could say whether an item allowed a wait or when it stopped taking orders
   * (070 closed that).
   */
  maxPerPerson: number | null
  allowsBackorder: boolean
  ordersCloseAt: string | null
  /** 129: the window's front edge. Before it, `orders_not_open`. */
  ordersOpenAt: string | null
  /**
   * 129: the stores whose answer DIFFERS from `allowsBackorder` — "every store
   * except these" when the default is on, "only these" when it is off. Only
   * differing rows come back; the server leaves out one that agrees.
   */
  backorderExceptions: { stockRoomId: string; roomName: string; allowsBackorder: boolean }[]
  /** 129: what a buyer must tick to order it, in order. Empty = nothing asked. */
  pledges: Pledge[]
  image: ShopImage | null
  isActive: boolean
  iMayEdit: boolean
  /**
   * EMPTY MEANS EVERYONE. Say that in words wherever this is rendered — an
   * empty list reads as "for nobody", which is the opposite of what it means
   * and would have a manager adding rows to fix a thing that is not broken.
   */
  audience: AudienceRow[]
  variants: ManagedVariant[]
}

/** 129: a statement the buyer ticks. The id is what the order path checks. */
export type Pledge = { pledgeId: string; statement: string }

function readPledges(raw: unknown): Pledge[] {
  return ((raw ?? []) as Record<string, unknown>[]).map((p) => ({
    pledgeId: p.pledge_id as string,
    statement: p.statement as string,
  }))
}

/** Parse the `axes` jsonb both reads return. */
function readAxes(raw: unknown): ItemAxis[] {
  return ((raw ?? []) as Record<string, unknown>[]).map((a) => ({
    axisId: a.axis_id as string,
    kind: (a.kind as AxisKind) ?? 'custom',
    label: a.label as string,
    position: (a.position as number) ?? 0,
    options: ((a.options ?? []) as Record<string, unknown>[]).map((o) => ({
      optionId: o.option_id as string,
      label: o.label as string,
      imagePath: (o.image_path as string) ?? null,
      imageWidth: (o.image_width as number) ?? null,
      imageHeight: (o.image_height as number) ?? null,
    })),
  }))
}

/**
 * Whether to offer this person the shop section at all.
 *
 * TWO CLAUSES, and the second is not optional: a room manager holds no
 * permission (064 made the managers a list, not a role), so a permission-only
 * check hides the shop from precisely the people who run it. Presentation only
 * — every function behind the screen re-checks shop.admin or
 * app.manages_stock_room itself, which is the actual boundary.
 */
export function canReachShopAdmin(me: Me): boolean {
  return can(me, 'shop.admin') || me.managesStockRoom
}

export async function loadStockRooms(): Promise<StockRoom[]> {
  const { data, error } = await supabase.rpc('stock_rooms')
  if (error) throw error
  return (data ?? []).map((r: Record<string, unknown>) => ({
    id: r.id as string,
    code: r.code as string,
    name: r.name as string,
    street: r.street as string,
    city: r.city as string,
    region: (r.region as string) ?? null,
    country: r.country as string,
    postalCode: (r.postal_code as string) ?? null,
    timezone: r.timezone as string,
    isActive: Boolean(r.is_active),
    iManage: Boolean(r.i_manage),
  }))
}

export async function loadRoomManagers(roomId?: string): Promise<RoomManager[]> {
  const { data, error } = await supabase.rpc('stock_room_managers', {
    p_room: roomId ?? null,
  })
  if (error) throw error
  return (data ?? []).map((r: Record<string, unknown>) => ({
    stockRoomId: r.stock_room_id as string,
    roomName: r.room_name as string,
    personId: r.person_id as string,
    displayName: (r.display_name as string) ?? 'Someone',
    addedAt: r.added_at as string,
  }))
}

export async function loadManagedItems(roomId?: string | null): Promise<ManagedItem[]> {
  const { data, error } = await supabase.rpc('shop_manage_items', { p_room: roomId ?? null })
  if (error) throw error
  return (data ?? []).map((r: Record<string, unknown>) => ({
    itemId: r.item_id as string,
    code: r.code as string,
    name: r.name as string,
    description: (r.description as string) ?? null,
    axes: readAxes(r.axes),
    priceStars: r.price_stars as number,
    ownerRoomId: (r.owner_room_id as string) ?? null,
    ownerRoomName: (r.owner_room_name as string) ?? null,
    maxPerPerson: (r.max_per_person as number) ?? null,
    allowsBackorder: Boolean(r.allows_backorder),
    ordersCloseAt: (r.orders_close_at as string) ?? null,
    ordersOpenAt: (r.orders_open_at as string) ?? null,
    backorderExceptions: ((r.backorder_exceptions ?? []) as Record<string, unknown>[]).map((x) => ({
      stockRoomId: x.stock_room_id as string,
      roomName: x.room_name as string,
      allowsBackorder: Boolean(x.allows_backorder),
    })),
    pledges: readPledges(r.pledges),
    image: readImage(r.image),
    isActive: Boolean(r.is_active),
    iMayEdit: Boolean(r.i_may_edit),
    audience: ((r.audience ?? []) as Record<string, unknown>[]).map((a) => ({
      eligibilityId: a.eligibility_id as string,
      orgUnitId: (a.org_unit_id as string) ?? null,
      personId: (a.person_id as string) ?? null,
      label: (a.label as string) ?? null,
    })),
    variants: ((r.variants ?? []) as Record<string, unknown>[]).map((v) => ({
      variantId: v.variant_id as string,
      label: v.label as string,
      sortOrder: (v.sort_order as number) ?? 0,
      isActive: Boolean(v.is_active),
      ownImage: v.own_image
        ? {
            path: (v.own_image as Record<string, unknown>).path as string,
            width: (v.own_image as Record<string, unknown>).width as number,
            height: (v.own_image as Record<string, unknown>).height as number,
          }
        : null,
      image: readImage(v.image),
      onHand: (v.on_hand as number) ?? 0,
      held: (v.held as number) ?? 0,
      free: (v.free as number) ?? 0,
      rooms: ((v.rooms ?? []) as Record<string, unknown>[]).map((s) => ({
        stockRoomId: s.stock_room_id as string,
        roomName: s.room_name as string,
        roomCity: s.room_city as string,
        onHand: s.on_hand as number,
        held: s.held as number,
        free: s.free as number,
      })),
    })),
  }))
}

export async function createStockRoom(input: {
  tenantId: string
  code: string
  name: string
  street: string
  city: string
  country: string
  region?: string | null
  postalCode?: string | null
  timezone: string
}): Promise<string> {
  const { data, error } = await supabase.rpc('create_stock_room', {
    p_tenant_id: input.tenantId,
    p_code: input.code,
    p_name: input.name,
    p_street: input.street,
    p_city: input.city,
    p_country: input.country,
    p_region: input.region ?? null,
    p_postal_code: input.postalCode ?? null,
    p_timezone: input.timezone,
  })
  if (error) throw error
  return data as string
}

/**
 * 071 — the write path 064 did not build.
 *
 * Every address in this app is TYPED (064's rule: a room is where a box sits,
 * never derived from where a person is based), which is to say every one of
 * them can carry a typo — and until this there was no way to fix one. The
 * Burnaby room shipped missing its unit number and could only be corrected by
 * a hand-written UPDATE.
 *
 * A REPLACE, NOT A PATCH, and every field is required for 049's reason: with
 * optional fields, "clear the region" and "leave the region alone" become the
 * same call and the silent outcome is data loss. Send the whole row back.
 *
 * `code` is deliberately absent. It is the stable key — an identifier you may
 * rewrite is one that stops identifying (067). Getting a code wrong means
 * closing the room and opening another, which is two visible acts.
 *
 * shop.admin only, and NOT a room manager: 064 made management a membership
 * list, so running a shelf and deciding where it is are different acts (021).
 * The server is the boundary; this signature is only the shape.
 */
export async function editStockRoom(input: {
  roomId: string
  name: string
  street: string
  city: string
  country: string
  timezone: string
  region: string | null
  postalCode: string | null
}): Promise<void> {
  const { error } = await supabase.rpc('edit_stock_room', {
    p_room: input.roomId,
    p_name: input.name,
    p_street: input.street,
    p_city: input.city,
    p_country: input.country,
    p_timezone: input.timezone,
    p_region: input.region,
    p_postal_code: input.postalCode,
  })
  if (error) throw error
}

export async function setStockRoomActive(roomId: string, active: boolean): Promise<void> {
  const { error } = await supabase.rpc('set_stock_room_active', {
    p_room: roomId,
    p_active: active,
  })
  if (error) throw error
}

export async function addRoomManager(roomId: string, personId: string): Promise<void> {
  const { error } = await supabase.rpc('add_stock_room_manager', {
    p_room: roomId,
    p_person: personId,
  })
  if (error) throw error
}

export async function removeRoomManager(roomId: string, personId: string): Promise<void> {
  const { error } = await supabase.rpc('remove_stock_room_manager', {
    p_room: roomId,
    p_person: personId,
  })
  if (error) throw error
}

export async function createShopItem(input: {
  tenantId: string
  code: string
  name: string
  priceStars: number
  axes?: AxisDraft[]
  ownerRoomId?: string | null
  description?: string | null
}): Promise<string> {
  const { data, error } = await supabase.rpc('create_shop_item', {
    p_tenant_id: input.tenantId,
    p_code: input.code,
    p_name: input.name,
    p_price_stars: input.priceStars,
    // Axes travel WITH the create, so an item never exists without its
    // combinations — 068's whole reason for collapsing two calls into one.
    p_axes: (input.axes ?? []).map((a) => ({ kind: a.kind, label: a.label, options: a.options })),
    p_owner_room_id: input.ownerRoomId ?? null,
    p_description: input.description ?? null,
    // 128: REC-24's category threshold is gone (David, 30 Sep) — the two
    // arguments no longer exist on the server, so nothing here sends them.
  })
  if (error) throw error
  return data as string
}

export async function setItemPrice(itemId: string, priceStars: number): Promise<void> {
  const { error } = await supabase.rpc('set_shop_item_price', {
    p_item: itemId,
    p_price_stars: priceStars,
  })
  if (error) throw error
}

/** What the form sends: the whole shape of an item, axes and options together. */
export type AxisDraft = { kind: AxisKind; label: string; options: string[] }

/**
 * THE WHOLE SHAPE IN ONE CALL — axes, their options, and the cross product.
 *
 * 068 replaced two functions with one deliberately. Declaring the axes and
 * generating the combinations separately left a window where an item existed
 * with its axes declared and no variants, which the screen then had to offer a
 * recovery path for; there is no half state to design around now.
 *
 * It is a REPLACE: an option not listed is deactivated, never deleted (a delete
 * takes the stock row with it), and the whole call refuses if the shape is
 * changing while stock is promised to an order. Returns the combination count.
 */
export async function setItemCatalogue(itemId: string, axes: AxisDraft[]): Promise<number> {
  const { data, error } = await supabase.rpc('set_shop_item_catalogue', {
    p_item: itemId,
    p_axes: axes.map((a) => ({ kind: a.kind, label: a.label, options: a.options })),
  })
  if (error) throw error
  return (data as number) ?? 0
}

/** The axis labels in render order — the same order the server labels a variant in. */
export function axisNames(axes: ItemAxis[]): string[] {
  return [...axes].sort((a, b) => a.position - b.position).map((a) => a.label)
}

/**
 * The default label for a standard kind. A person may rename any of them; this
 * is only what the field is called before they do.
 */
export const AXIS_KIND_LABEL: Record<AxisKind, string> = {
  colour: 'Colour',
  size: 'Size',
  // NOT "Gender". See the ItemAxis header — this names a garment, not a person.
  fit: 'Fit',
  custom: '',
}

/**
 * "S, M, L" — or one per line — into a list.
 *
 * Splitting on commas AND newlines because both are what people actually type,
 * and a person pasting a column out of a spreadsheet should not have to think
 * about it. Blanks dropped, duplicates collapsed, order kept.
 *
 * `app.clean_options` does this again server-side and that is deliberate, not
 * duplication: this one exists so the form can COUNT the combinations before
 * the press ("2 colours × 3 sizes = 6"), and the database's is the one that
 * decides. If they ever disagree the database wins and the preview was wrong,
 * which is the harmless direction.
 */
export function parseOptions(raw: string): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const part of raw.split(/[,\n]/)) {
    const v = part.trim()
    if (v === '' || seen.has(v.toLowerCase())) continue
    seen.add(v.toLowerCase())
    out.push(v)
  }
  return out
}

/**
 * How many variants a set of axes will produce.
 *
 * An axis with no options contributes ONE, not zero — otherwise the count reads
 * zero while somebody is still filling the form in, which is most of the time.
 * An item with no axes at all is one combination, not none: it still has a
 * single variant for its stock to live on.
 */
export function combinationCount(axes: { options: string[] }[]): number {
  return axes.reduce((n, a) => n * Math.max(1, a.options.length), 1)
}

/**
 * ON HAND IS SET, NEVER ADJUSTED BY A DELTA.
 *
 * 065's rule, restated here because the client is where somebody will be
 * tempted to add a "+1" button: a delta is how two managers counting one shelf
 * produce a number neither of them saw. What goes to the database is what
 * somebody observed when they looked at it.
 */
export async function setStock(variantId: string, roomId: string, onHand: number): Promise<void> {
  const { error } = await supabase.rpc('set_shop_stock', {
    p_variant: variantId,
    p_room: roomId,
    p_on_hand: onHand,
  })
  if (error) throw error
}

/* ═══════════════════════════════════════════════════════════════════════════
   THE ROOM'S OWN SHELF (074).

   Everything above is ITEM-first: an item, and the rooms hanging off each of
   its combinations. That answers "where in the world is this jacket" and is the
   wrong shape for the question somebody standing in Burnaby actually has, which
   is WHAT IS ON MY SHELF. `stock_room_inventory` starts from the room.

   ── IT REFUSES RATHER THAN RETURNING AN EMPTY LIST ──────────────────────────

   Somebody who does not run the room gets an error, not a short list. 043's
   argument: `shop_order`'s RLS is already manages_stock_room, so a silent read
   would come back with `awaiting` 0 on every row and render as "nobody is
   waiting" — a false claim about the world rather than a true one about the
   caller. Callers must show the refusal, never swallow it into an empty state.
   ═══════════════════════════════════════════════════════════════════════════ */

/** One custom axis on one combination — "Sleeve: Long". */
export type ExtraOption = { label: string; value: string }

export type RoomInventoryRow = {
  /** 075 — several rooms in one read, so every row says which shelf it is. */
  stockRoomId: string
  roomName: string
  roomCity: string
  variantId: string
  /**
   * The rendered combination. KEPT beside the split columns rather than
   * replaced by them, and the redundancy is deliberate: this is the string
   * `shop_order` snapshots and the Store shows a buyer, so a manager has to be
   * able to see the same words somebody ordered. The columns are for scanning
   * and filtering; the label is what people say out loud.
   */
  variantLabel: string
  itemId: string
  itemName: string
  itemCode: string
  /** A delisted item's boxes are still real and still on this shelf (066). */
  itemIsActive: boolean
  variantIsActive: boolean
  /**
   * 075 — the standard axes as their own values, and this is the payoff for
   * 068's `kind`. NULL where the item does not vary that way, which is the
   * normal case. **Resolved from the axis kind in the database, never by
   * splitting `variantLabel`** — an option containing " · " or a reordered
   * axis (REC-29) breaks a parser silently and in the direction nobody checks.
   */
  size: string | null
  colour: string | null
  fit: string | null
  /**
   * Custom axes, in position order. They get ONE column between them because a
   * custom axis has no identity across items — the shirt's "Sleeve" and a mug's
   * "Handle" are unrelated, and an item may declare several — so a column each
   * would be a table whose shape changed with the rows in it.
   */
  extra: ExtraOption[]
  /**
   * 076 — computed by `app.size_rank` so the client can sort by size without a
   * second definition of what order sizes go in. L never sorts before M, and
   * the one place that is decided stays the database (067).
   */
  sizeRank: number
  onHand: number
  /** Promised to an order that has not been collected. Never typed (065). */
  held: number
  free: number
  /** NULL = nobody is watching this shelf. 0 = tell me when it is empty. */
  reorderLevel: number | null
  /**
   * FREE is at or below the level — never on_hand. Held stock is already
   * promised, so three on the shelf with three held is an empty shelf for the
   * next person through the door. Derived in the database (074), never here.
   */
  needsReorder: boolean
  /**
   * The OTHER half of `held`, and David's "ordered/assigned" is both of them.
   * Held is a box with somebody's name on it; awaiting is a name with no box —
   * 069's backorder queue. A screen showing one answers half the question.
   */
  awaiting: number
  /** When the count last moved. The modal's "recently stocked here" reads it. */
  updatedAt: string | null
  image: ShopImage | null
}

/**
 * SEVERAL ROOMS IN ONE CALL, and a room you do not manage refuses all of it.
 *
 * 046's argument for server-side: one request per room cannot restore an ORDER
 * BY that has already been truncated, and here it would also have to re-group
 * by item. And 074's refusal is sharpened rather than kept — with four rooms
 * selected, quietly returning three leaves a table that still looks full.
 */
export async function loadRoomInventory(roomIds: string[]): Promise<RoomInventoryRow[]> {
  // EMPTY MEANS EVERY ROOM YOU MANAGE (076, David's default). The early return
  // that used to sit here short-circuited exactly that, so the screen would
  // have shown nothing where the database was ready to show everything.
  const { data, error } = await supabase.rpc('stock_room_inventory', {
    p_rooms: roomIds.length === 0 ? null : roomIds,
  })
  if (error) throw error
  return (data ?? []).map((r: Record<string, unknown>) => ({
    stockRoomId: r.stock_room_id as string,
    roomName: r.room_name as string,
    roomCity: r.room_city as string,
    variantId: r.variant_id as string,
    variantLabel: r.variant_label as string,
    itemId: r.item_id as string,
    itemName: r.item_name as string,
    itemCode: r.item_code as string,
    itemIsActive: Boolean(r.item_is_active),
    variantIsActive: Boolean(r.variant_is_active),
    size: (r.size_option as string) ?? null,
    colour: (r.colour_option as string) ?? null,
    fit: (r.fit_option as string) ?? null,
    extra: ((r.extra_options ?? []) as Record<string, unknown>[]).map((o) => ({
      label: o.label as string,
      value: o.value as string,
    })),
    sizeRank: (r.size_rank as number) ?? 0,
    onHand: (r.on_hand as number) ?? 0,
    held: (r.held as number) ?? 0,
    free: (r.free as number) ?? 0,
    reorderLevel: (r.reorder_level as number) ?? null,
    needsReorder: Boolean(r.needs_reorder),
    awaiting: (r.awaiting as number) ?? 0,
    updatedAt: (r.updated_at as string) ?? null,
    image: readImage(r.image),
  }))
}

/**
 * EVERY COMBINATION OF ONE ITEM, COUNTED IN ONE PRESS (075).
 *
 * Twenty combinations was twenty saves, which is why nobody would have used the
 * grid. Returns how many rows actually moved, so the screen can say "4 of 20
 * changed" rather than implying it rewrote the shelf — the server skips
 * unchanged lines, because twenty `counted` events for a change to one is how
 * an audit log stops being readable.
 *
 * **A REFUSAL ON ANY LINE APPLIES NONE OF IT.** That is what one commit means,
 * and it is the whole reason this is a function rather than a loop here: a loop
 * in the browser leaves a grid where some rows took and some did not, with
 * nothing saying which.
 *
 * **STILL AN ABSOLUTE COUNT, NEVER A DELTA.** 065's rule, and this is the
 * screen where it will be argued — see REC-41, which is filed rather than
 * built. Do not add a "+" to this signature to settle it.
 */
export async function setStockBatch(
  roomId: string,
  counts: { variantId: string; onHand: number }[],
): Promise<number> {
  const { data, error } = await supabase.rpc('set_shop_stock_batch', {
    p_room: roomId,
    p_counts: counts.map((c) => ({ variant: c.variantId, on_hand: c.onHand })),
  })
  if (error) throw error
  return (data as number) ?? 0
}

/**
 * WHAT TO RESTOCK HERE, from the shelf read the table already has — grouped by
 * WHY, and saying which combinations (REC-65, David 4 Oct: "the 'needs
 * attention' bit … is messy and doesn't say why an item needs attention").
 *
 * Deliberately NOT a second function: `needsReorder` is 074's rule and a second
 * definition of "low" is how the modal and the table come to disagree about the
 * same shelf.
 *
 * ── FOUR GROUPS, AND ONLY THREE OF THEM ARE "ATTENTION" ─────────────────────
 *
 *   1. waiting — somebody ordered it here and there is no box. The most urgent,
 *      and the one the first cut ignored although the read carried `awaiting`.
 *   2. out     — an active combination with nothing free.
 *   3. low     — free at or below the reorder level (074's rule, from the read).
 *   4. recent  — counted lately. A CONVENIENCE, never a reason: it sat under
 *      "Needs attention" before, which is how an item that needed nothing
 *      appeared to need something. It now has its own plain heading.
 *
 * Each item appears ONCE, under its strongest reason — an item out of one size
 * and waited on in another is one thing to restock, not two offers.
 *
 * ── THE COMBINATIONS ARE NAMED ──────────────────────────────────────────────
 *
 * "3 of 40 out of stock" made somebody open the item to learn which three. The
 * combinations are listed (the variant label, which is what a buyer ordered),
 * the first few and then "+N".
 *
 * ── WHAT THE READ CANNOT SEE ────────────────────────────────────────────────
 *
 * `stock_room_inventory` starts at `shop_stock`, so an order waiting at a store
 * that has NEVER held that combination has no row here and is not counted. That
 * is exactly a pre-order at a store not yet stocked. Fixing it is the read's
 * job (a migration), not this function's — filed with REC-65.
 *
 * Retired combinations are left out of "out" and "low" (nobody may buy them,
 * so an empty shelf is correct) but not out of "waiting" — an order is an order.
 * Delisted items are excluded entirely, as `stock_reorder_alerts` excludes them.
 *
 * Pure, so the render suite asserts the wording directly.
 */
export type StockReason = 'waiting' | 'out' | 'low' | 'recent'

export type StockSuggestion = {
  itemId: string
  itemName: string
  reason: StockReason
  /** Already worded: "4 orders waiting", "3 of 40 out", "None on the shelf". */
  detail: string
  /** The combinations it is about, worded: "Navy · M ×3, Navy · L". Empty for recent. */
  combos: string
}

export type StockSuggestionGroup = {
  reason: StockReason
  heading: string
  items: StockSuggestion[]
  /** Items in this group that were not listed. Said, never silently dropped. */
  more: number
}

export const STOCK_REASON_HEADING: Record<StockReason, string> = {
  waiting: 'People waiting',
  out: 'Out of stock',
  low: 'Below the reorder level',
  recent: 'Recently counted here',
}

/** How many combinations are named before "+N". */
export const COMBOS_SHOWN = 4

export function nameCombos(labels: string[], shown = COMBOS_SHOWN): string {
  if (labels.length <= shown) return labels.join(', ')
  return `${labels.slice(0, shown).join(', ')} +${labels.length - shown}`
}

export function suggestStock(
  rows: RoomInventoryRow[],
  { perGroup = 8, recent = 4, now = Date.now() }: { perGroup?: number; recent?: number; now?: number } = {},
): StockSuggestionGroup[] {
  const byItem = new Map<string, RoomInventoryRow[]>()
  for (const r of rows) {
    if (!r.itemIsActive) continue
    const list = byItem.get(r.itemId)
    if (list) list.push(r)
    else byItem.set(r.itemId, [r])
  }

  type Ranked = StockSuggestion & { weight: number; recency: number }
  const buckets: Record<StockReason, Ranked[]> = { waiting: [], out: [], low: [], recent: [] }

  for (const [itemId, list] of byItem) {
    const itemName = list[0].itemName
    const live = list.filter((r) => r.variantIsActive)
    const touched = Math.max(0, ...list.map((r) => (r.updatedAt ? Date.parse(r.updatedAt) : 0)))

    const waiting = list.filter((r) => r.awaiting > 0).sort((a, b) => b.awaiting - a.awaiting)
    const gone = live.filter((r) => r.free === 0)
    const low = live.filter((r) => r.needsReorder && r.free > 0)

    if (waiting.length > 0) {
      const n = waiting.reduce((s, r) => s + r.awaiting, 0)
      buckets.waiting.push({
        itemId, itemName, reason: 'waiting', weight: n, recency: touched,
        // One order is one unit (069), so this is a count of boxes too.
        detail: `${n} ${n === 1 ? 'order' : 'orders'} waiting`,
        combos: nameCombos(waiting.map((r) => (r.awaiting > 1 ? `${r.variantLabel} ×${r.awaiting}` : r.variantLabel))),
      })
    } else if (gone.length > 0) {
      const all = gone.length === live.length
      buckets.out.push({
        itemId, itemName, reason: 'out', weight: all ? 1e6 : gone.length, recency: touched,
        detail: all ? 'None on the shelf' : `${gone.length} of ${live.length} out`,
        combos: all && live.length === 1 ? '' : nameCombos(gone.map((r) => r.variantLabel)),
      })
    } else if (low.length > 0) {
      buckets.low.push({
        itemId, itemName, reason: 'low', weight: low.length, recency: touched,
        detail: `${low.length} below the reorder level`,
        combos: nameCombos(low.map((r) => `${r.variantLabel} (${r.free} left, level ${r.reorderLevel})`), 3),
      })
    } else if (touched > 0) {
      buckets.recent.push({
        itemId, itemName, reason: 'recent', weight: 0, recency: touched,
        detail: 'Counted ' + countedWhen(touched, now), combos: '',
      })
    }
  }

  const order: StockReason[] = ['waiting', 'out', 'low', 'recent']
  return order
    .map((reason) => {
      const list = buckets[reason].sort((a, b) =>
        reason === 'recent'
          ? b.recency - a.recency || a.itemName.localeCompare(b.itemName)
          : b.weight - a.weight || b.recency - a.recency || a.itemName.localeCompare(b.itemName))
      const cap = reason === 'recent' ? recent : perGroup
      return {
        reason,
        heading: STOCK_REASON_HEADING[reason],
        items: list.slice(0, cap).map(({ itemId, itemName, reason: r, detail, combos }) =>
          ({ itemId, itemName, reason: r, detail, combos })),
        // Recent is a shortcut, so its overflow is not news; attention's is.
        more: reason === 'recent' ? 0 : Math.max(0, list.length - cap),
      }
    })
    .filter((g) => g.items.length > 0)
}

/** "today", "yesterday", "3 days ago", "on 14 Sep" — for the recent line only. */
export function countedWhen(ms: number, now = Date.now()): string {
  const days = Math.floor((now - ms) / 86_400_000)
  if (days <= 0) return 'today'
  if (days === 1) return 'yesterday'
  if (days < 14) return `${days} days ago`
  return 'on ' + new Date(ms).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
}

/**
 * Ctrl+K, or Cmd+K on a Mac.
 *
 * **THE BROWSER TAKES THIS SHORTCUT TOO** — Chrome and Firefox both focus the
 * address bar in search mode on Ctrl+K — so the handler must `preventDefault`.
 * This comment used to rule a global Ctrl+K out ("it would take the address
 * bar away from ~900 people"). David reversed that on 1 Nov (late): Ctrl+K
 * (⌘K on a Mac) is now the hub-wide Ask One key (lib/askOneKey.ts), because
 * the address-bar search is reachable by Ctrl+L and typing, while the hub's
 * one assistant had no key people already know. The two coexist: AppShell's
 * handler stands down while a dialog traps focus ([aria-modal="true"]), so
 * inside AddStock's modal this one owns the key.
 *
 * Exported as a predicate because the render suite has no DOM: this is the half
 * of the shortcut a test can actually reach.
 */
export function isCommandKey(e: { key: string; ctrlKey: boolean; metaKey: boolean }): boolean {
  return (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k'
}

/**
 * Does this shelf row match a typed query?
 *
 * ── EVERY TERM MUST MATCH, WHICH IS WHY "m grey" USED TO FIND NOTHING ───────
 *
 * The first cut tested the whole query as one substring, so a single word
 * worked and two words could only match if they appeared together in that
 * order. Splitting on whitespace and requiring EVERY term to hit SOMETHING is
 * what people mean by typing two words (047's argument for Wiki One, one
 * domain over).
 *
 * ── A SHORT TERM IS ANCHORED TO A WORD; A LONG ONE IS NOT ───────────────────
 *
 * Plain substring matching would make "m" match Lima, Medium and Women's, so
 * "m grey" would return every grey row in Lima. Word-prefix alone would lose
 * "rey" for Grey, which is the kind of near-miss that makes a search feel
 * broken. So: **one or two characters must start a word** — which is exactly
 * the size vocabulary, M, L, XL, 2XL — and three or more may match anywhere.
 */
export function variantMatches(row: RoomInventoryRow, query: string): boolean {
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean)
  if (terms.length === 0) return true
  const fields = [
    row.variantLabel, row.size, row.colour, row.fit, row.itemName, row.roomName,
    ...row.extra.flatMap((e) => [e.label, e.value]),
  ].map((f) => (f ?? '').toLowerCase()).filter(Boolean)

  return terms.every((t) =>
    fields.some((f) =>
      t.length >= 3
        ? f.includes(t)
        // Split on the separators a label can carry, so "Navy · M" gives two
        // words rather than one and "m" anchors to the size.
        : f.split(/[\s·,/-]+/).some((w) => w.startsWith(t)),
    ),
  )
}

/**
 * How the shelf is ordered. David asked for item, store, colour and size.
 *
 * **SIZE SORTS ON `sizeRank`, WHICH THE DATABASE COMPUTED** — 067's rule that L
 * never sorts before M, and the reason the rank is a returned column rather
 * than something this file works out: a second definition of size order is the
 * one thing a general axis model was allowed to cost, and only if `kind` kept
 * it in one place.
 *
 * Client-side is honest here for the reason the filters are: `stock_room_inventory`
 * takes no limit, so the caller holds every row and reordering them cannot drop
 * a match. The moment that read gains a limit, this moves to the server.
 */
export type ShelfSort = 'item' | 'store' | 'colour' | 'size'

export function sortRows(rows: RoomInventoryRow[], by: ShelfSort): RoomInventoryRow[] {
  const txt = (s: string | null) => s ?? ''
  // A copy: sorting the array the caller handed us mutates React state in place
  // and the re-render then has nothing to notice.
  return [...rows].sort((a, b) => {
    switch (by) {
      case 'store':
        return a.roomName.localeCompare(b.roomName)
          || a.itemName.localeCompare(b.itemName)
          || a.sizeRank - b.sizeRank
      case 'colour':
        // Rows with no colour go LAST rather than first: most items have none,
        // and opening on a block of dashes reads as a broken load (054's rule
        // for unplaced people).
        return (txt(a.colour) ? 0 : 1) - (txt(b.colour) ? 0 : 1)
          || txt(a.colour).localeCompare(txt(b.colour))
          || a.itemName.localeCompare(b.itemName)
          || a.sizeRank - b.sizeRank
      case 'size':
        return (txt(a.size) ? 0 : 1) - (txt(b.size) ? 0 : 1)
          || a.sizeRank - b.sizeRank
          || a.itemName.localeCompare(b.itemName)
      case 'item':
      default:
        return a.itemName.localeCompare(b.itemName)
          || a.roomName.localeCompare(b.roomName)
          || a.sizeRank - b.sizeRank
    }
  })
}

/**
 * EDIT AN ITEM — the write path `create_shop_item` never had a partner for.
 *
 * A REPLACE with no defaults (049), so clearing the description is expressible
 * and is not the same call as leaving it alone. `code` is absent: it is the
 * stable key (067), the same line `editStockRoom` draws.
 *
 * Changing the OWNING ROOM is `shop.admin` server-side, because that column
 * decides who may price the item (065) — running a shelf and deciding who
 * prices the catalogue are different acts (021).
 */
export async function editShopItem(input: {
  itemId: string
  name: string
  description: string | null
  ownerRoomId: string | null
}): Promise<void> {
  const { error } = await supabase.rpc('edit_shop_item', {
    p_item: input.itemId,
    p_name: input.name,
    p_description: input.description,
    p_owner_room_id: input.ownerRoomId,
  })
  if (error) throw error
}

/**
 * MOVE BOXES BETWEEN STORES — and it is not two counts.
 *
 * Two `setStock` calls would be two independent observations that happen to add
 * up, logged as unrelated recounts, with a window where the units are in
 * neither room or both. One function, one transaction.
 *
 * **ONLY FREE STOCK MOVES.** Held units are promised to a named person waiting
 * at that counter (069); moving them relocates somebody's jacket silently.
 * Returns how many waiting orders the arrival claimed at the far end — a
 * transfer is a delivery to whoever is queued there.
 */
export async function transferStock(input: {
  variantId: string
  fromRoomId: string
  toRoomId: string
  quantity: number
}): Promise<number> {
  const { data, error } = await supabase.rpc('transfer_shop_stock', {
    p_variant: input.variantId,
    p_from: input.fromRoomId,
    p_to: input.toRoomId,
    p_qty: input.quantity,
  })
  if (error) throw error
  return (data as number) ?? 0
}

/**
 * What is below its level, across every room the caller runs.
 *
 * This is the payoff, and without it a reorder level is decoration: a threshold
 * you only discover by visiting each of sixteen rooms in turn is one nobody
 * discovers in time. SELF-SCOPED BY SIGNATURE — no room argument and no person
 * argument — so it cannot become a league table of whose shelves run dry.
 */
export type ReorderAlert = {
  stockRoomId: string
  roomName: string
  roomCity: string
  variantId: string
  variantLabel: string
  itemId: string
  itemName: string
  onHand: number
  held: number
  free: number
  reorderLevel: number
  awaiting: number
  /** level − free. The list is ordered by this: what to order first. */
  shortfall: number
}

export async function loadReorderAlerts(): Promise<ReorderAlert[]> {
  const { data, error } = await supabase.rpc('stock_reorder_alerts')
  if (error) throw error
  return (data ?? []).map((r: Record<string, unknown>) => ({
    stockRoomId: r.stock_room_id as string,
    roomName: r.room_name as string,
    roomCity: r.room_city as string,
    variantId: r.variant_id as string,
    variantLabel: r.variant_label as string,
    itemId: r.item_id as string,
    itemName: r.item_name as string,
    onHand: (r.on_hand as number) ?? 0,
    held: (r.held as number) ?? 0,
    free: (r.free as number) ?? 0,
    reorderLevel: (r.reorder_level as number) ?? 0,
    awaiting: (r.awaiting as number) ?? 0,
    shortfall: (r.shortfall as number) ?? 0,
  }))
}

/**
 * A SEPARATE call from setStock, and that is the decision rather than an
 * omission.
 *
 * Folding the level into `set_shop_stock` would make one call mean two acts —
 * "I counted the shelf" and "I decided when to reorder" — and 049's replace
 * rule then bites: every count typed without a level would silently clear one.
 * They are set on different occasions, by different reasoning, and assertion 15
 * of the 074 suite fails if anybody merges them.
 *
 * `null` clears it. Clearing must stay expressible: stopping watching a shelf
 * is a real decision, and without it the only way out is a level of zero, which
 * means the opposite.
 */
export async function setReorderLevel(
  variantId: string, roomId: string, level: number | null,
): Promise<void> {
  const { error } = await supabase.rpc('set_shop_reorder_level', {
    p_variant: variantId,
    p_room: roomId,
    p_level: level,
  })
  if (error) throw error
}

export async function setItemActive(itemId: string, active: boolean): Promise<void> {
  const { error } = await supabase.rpc('set_shop_item_active', {
    p_item: itemId,
    p_active: active,
  })
  if (error) throw error
}

/**
 * Every ordering fact about an item, in ONE call (069, reshaped by 129).
 *
 * A REPLACE WITH NO DEFAULTS on the server (049's rule), so this always sends
 * all five: omitting one would silently clear it, and "leave the close date
 * alone" and "clear the close date" must not be the same call. The exceptions
 * REPLACE the item's room overrides wholesale — a room manager's own override
 * included — and `[]` means "every store follows the default".
 *
 * THERE IS NO `isPreorder`. A pre-order is backorder plus an orders window —
 * independent facts, each useful alone (David, 14 Sep; 129 kept it so).
 */
export async function setItemOrdering(
  itemId: string,
  rules: {
    maxPerPerson: number | null
    allowsBackorder: boolean
    backorderExceptions: string[]
    ordersOpenAt: string | null
    ordersCloseAt: string | null
  },
): Promise<void> {
  const { error } = await supabase.rpc('set_shop_item_ordering', {
    p_item: itemId,
    p_max_per_person: rules.maxPerPerson,
    p_allows_backorder: rules.allowsBackorder,
    p_backorder_exceptions: rules.backorderExceptions,
    p_orders_open_at: rules.ordersOpenAt,
    p_orders_close_at: rules.ordersCloseAt,
  })
  if (error) throw error
}

/**
 * 129: the statements a buyer must tick. A REPLACE: `[]` removes them all. An
 * unchanged statement keeps its id server-side, so re-saving the same words
 * does not invalidate a buyer's open page.
 */
export async function setItemPledges(itemId: string, statements: string[]): Promise<void> {
  const { error } = await supabase.rpc('set_shop_item_pledges', {
    p_item: itemId,
    p_statements: statements,
  })
  if (error) throw error
}

/**
 * 129: one row per combination per store with a live order — what to order
 * from the supplier. The server REFUSES anybody who may not edit the item
 * rather than handing a room manager their own rooms as though it were all.
 */
export type DemandRow = {
  stockRoomId: string
  roomName: string
  roomCity: string
  roomCountry: string
  variantId: string
  variantLabel: string
  size: string | null
  colour: string | null
  fit: string | null
  sizeRank: number | null
  sortOrder: number
  /** Ordered and waiting for stock — the number the supplier order is sized to. */
  awaiting: number
  /** A box with a name on it. */
  held: number
  collected: number
  /** On the shelf and promised to nobody. */
  free: number
}

export async function loadItemDemand(itemId: string): Promise<DemandRow[]> {
  const { data, error } = await supabase.rpc('shop_item_demand', { p_item: itemId })
  if (error) throw error
  return (data ?? []).map((r: Record<string, unknown>) => ({
    stockRoomId: r.stock_room_id as string,
    roomName: r.room_name as string,
    roomCity: r.room_city as string,
    roomCountry: r.room_country as string,
    variantId: r.variant_id as string,
    variantLabel: r.variant_label as string,
    size: (r.size_option as string) ?? null,
    colour: (r.colour_option as string) ?? null,
    fit: (r.fit_option as string) ?? null,
    sizeRank: (r.size_rank as number) ?? null,
    sortOrder: (r.sort_order as number) ?? 0,
    awaiting: (r.awaiting as number) ?? 0,
    held: (r.held as number) ?? 0,
    collected: (r.collected as number) ?? 0,
    free: (r.free as number) ?? 0,
  }))
}

/**
 * The tally as CSV, one row per store per combination — the long shape a
 * supplier's spreadsheet pivots, rather than a grid that has to be unpicked.
 * Quoted throughout because store names and option labels are typed by people.
 */
export function demandCsv(itemName: string, rows: DemandRow[]): string {
  const q = (v: string | number | null) => {
    const t = v === null ? '' : String(v)
    return `"${t.replace(/"/g, '""')}"`
  }
  const head = ['Item', 'Store', 'City', 'Country', 'Combination', 'Size', 'Colour', 'Fit',
    'Waiting', 'Reserved', 'Collected', 'Free on shelf']
  const lines = rows.map((r) =>
    [itemName, r.roomName, r.roomCity, r.roomCountry, r.variantLabel, r.size, r.colour, r.fit,
      r.awaiting, r.held, r.collected, r.free].map(q).join(','))
  return [head.map(q).join(','), ...lines].join('\r\n') + '\r\n'
}

/* ═══════════════════════════════════════════════════════════════════════════
   IMAGES (070). Three levels, one resolution, and it is not in this file.

   `app.shop_image_for` ranks variant → option → item and breaks a tie on axis
   POSITION. Everything here uploads, sets and clears; nothing here decides
   which picture wins. A second ranking in TypeScript would disagree with the
   server the first time somebody reorders an axis.
   ═══════════════════════════════════════════════════════════════════════════ */

/** Exactly one target, matching set_shop_image's own rule. */
export type ImageTarget =
  | { itemId: string }
  | { optionId: string }
  | { variantId: string }

export async function setShopImage(
  target: ImageTarget,
  image: { path: string; width: number; height: number } | null,
): Promise<void> {
  const { error } = await supabase.rpc('set_shop_image', {
    p_item: 'itemId' in target ? target.itemId : null,
    p_option: 'optionId' in target ? target.optionId : null,
    p_variant: 'variantId' in target ? target.variantId : null,
    p_path: image?.path ?? null,
    p_width: image?.width ?? null,
    p_height: image?.height ?? null,
  })
  if (error) throw error
}

/**
 * Upload one prepared image for an item.
 *
 * ── THE PATH SHAPE IS THE PERMISSION, AND IT IS NOT 019'S ───────────────────
 *
 * `<tenant>/<item>/<uuid>.jpg`. 070's Storage policies read the SECOND segment
 * as the item and ask `app.may_edit_shop_item`, so changing this shape breaks
 * uploads with a permission error that reads like a session problem.
 *
 * post-media keys its second segment on the PERSON, deliberately differently: a
 * site photograph belongs to whoever took it, and a product shot belongs to the
 * item — the next person to run that room has to be able to replace it.
 *
 * The prepared blob comes from `preparePhoto`, which resizes AND strips EXIF as
 * one inseparable act (019). A swag photo is unlikely to carry a client site's
 * coordinates, but it is somebody's phone and the same function costs nothing.
 */
export async function uploadShopImage(
  prepared: { file: Blob; width: number; height: number },
  tenantId: string,
  itemId: string,
): Promise<{ path: string; width: number; height: number }> {
  const path = `${tenantId}/${itemId}/${crypto.randomUUID()}.jpg`
  const { error } = await supabase.storage.from('shop-media').upload(path, prepared.file, {
    contentType: 'image/jpeg',
    upsert: false,
  })
  if (error) throw error
  return { path, width: prepared.width, height: prepared.height }
}

/**
 * A displayable URL. The bucket is PRIVATE (070), so every render signs.
 *
 * One hour, matching `signedPhotoUrl` — long enough that browsing the shop
 * never expires an image mid-session, short enough that a pasted URL is dead.
 */
export async function signedShopUrl(path: string): Promise<string | null> {
  const { data, error } = await supabase.storage.from('shop-media').createSignedUrl(path, 3600)
  if (error) return null
  return data?.signedUrl ?? null
}

/**
 * Where a rendered picture came from, in words.
 *
 * The manager screen shows the SAME image on four combinations and somebody has
 * to be able to tell why. Without this the honest question "where do I change
 * that photo" has no answer on the page.
 */
export function imageSourceLabel(image: ShopImage | null): string {
  if (!image) return 'No picture'
  switch (image.source) {
    case 'variant': return 'This combination only'
    case 'option': return `From ${image.optionLabel ?? 'an option'}`
    case 'item': return 'The item default'
  }
}

export async function addItemAudience(
  itemId: string,
  target: { orgUnitId?: string | null; personId?: string | null },
): Promise<string> {
  const { data, error } = await supabase.rpc('add_shop_item_audience', {
    p_item: itemId,
    p_org_unit_id: target.orgUnitId ?? null,
    p_person_id: target.personId ?? null,
  })
  if (error) throw error
  return data as string
}

export async function removeItemAudience(eligibilityId: string): Promise<void> {
  const { error } = await supabase.rpc('remove_shop_item_audience', {
    p_eligibility: eligibilityId,
  })
  if (error) throw error
}

/**
 * The item code, and why a person should never have to type one.
 *
 * `shop_item_code_check` is `^[a-z0-9][a-z0-9_-]{1,40}$` — lower case, no
 * spaces, at least two characters. The first form asked for a "short code" and
 * only lowercased it, so typing the obvious thing ("Softshell jacket") produced
 * `softshell jacket` and the DATABASE refused it, with a constraint name in
 * front of somebody trying to add a mug.
 *
 * A code is a machine key with a uniqueness constraint on it. The name is the
 * thing a human actually knows. So the code is DERIVED from the name and shown
 * rather than asked for — editable, because a person who wants `jacket-v2`
 * should have it, but never blank and never wrong by default.
 *
 * The regex lives here ONCE and both the slug and the validator use it, so the
 * form cannot come to disagree with the constraint it is protecting. If the
 * constraint ever changes, this is the one line that changes with it.
 */
export const ITEM_CODE_RE = /^[a-z0-9][a-z0-9_-]{1,40}$/

export function slugifyCode(name: string): string {
  const slug = name
    .normalize('NFKD')
    // Accents out rather than dropped as whole letters: "Peñuelas" becomes
    // "penuelas", not "peuelas". app.normalise_tag does the same thing to tags
    // server-side, for the same reason.
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 41)
    // A trailing hyphen can survive the slice.
    .replace(/-+$/, '')
  return slug
}

/**
 * Why this code would be refused, or null. The sentence describes the RULE,
 * never the constraint — "shop_item_code_check" tells a stock manager nothing.
 */
export function codeProblem(code: string): string | null {
  if (code === '') return 'A code is needed. It is filled in from the name, so clearing the name clears this too.'
  if (code.length < 2) return 'A code needs at least two characters.'
  if (!ITEM_CODE_RE.test(code)) {
    return 'A code can use lower-case letters, numbers, hyphens and underscores only, and must start with a letter or a number.'
  }
  return null
}

/**
 * A count that will be refused before it is sent.
 *
 * `shop_stock_hold_within_hand` forbids on_hand below what is already promised,
 * and the manager's next act when they hit it is to hand those items over, not
 * to argue with a constraint name. Returning the sentence rather than a boolean
 * is app.shop_item_block_reason's shape: the screen renders the reason and does
 * not get to invent a different story from the same numbers.
 */
export function stockRefusal(onHand: number, held: number): string | null {
  if (!Number.isFinite(onHand) || onHand < 0) return 'A count cannot be negative.'
  if (onHand < held) {
    return `${held} ${held === 1 ? 'is' : 'are'} already promised to somebody. Hand those over first, or cancel the orders.`
  }
  return null
}

/**
 * IANA zones offered when typing a room's address.
 *
 * Rule 9, and 064 REFUSES an invalid name rather than defaulting it — unlike
 * 023's notification chain, which defaults because BambooHR sends what it
 * sends. A stock room is typed by an admin who can be told they are wrong.
 *
 * A list rather than a free-text box for the same reason the room's address is
 * typed and not derived: this is a short list that grows when a room opens, and
 * a typo in a zone is a handover time that is wrong by hours with nothing
 * saying so. It is not exhaustive and is not meant to be — add the zone when
 * the room opens.
 */
/* ═══════════════════════════════════════════════════════════════════════════
   THE SHOP FLOOR — 069, REC-11.

   Everything above this line is the manager's side. Below it is what a person
   with stars actually uses, and the two must not be mixed: see the two-reads
   note at the top of this file.

   ── A ROOM IS CHOSEN FIRST, AND THAT IS NOT A CONVENIENCE ───────────────────

   `free` and `can_backorder` are both properties of an item AND a room. Asking
   the catalogue without one gives the total across every room, which is the
   right answer to "does this exist anywhere" and the wrong answer to "can I
   have one". The screen therefore picks a room before it renders a price, and
   passes it on every read.

   A room is an ADDRESS, not a person's location (064) — nothing in the roster
   says where somebody is based in a way a shelf could be derived from, and
   seeding one from the other puts swag where there is no shelf. So the person
   chooses, deliberately, and the default is only a default.
   ═══════════════════════════════════════════════════════════════════════════ */

/**
 * Somewhere this combination can actually be got (072).
 *
 * A room appears here if it has FREE STOCK or will take a BACKORDER. The second
 * half is why the database builds this from `stock_room` rather than
 * `shop_stock`: 069 made backorder a POLICY, so a room may be willing to order
 * something in that it has never held, and a list built from stock rows cannot
 * see those rooms at all.
 *
 * Closed rooms are never here — a shut shelf is not a collection point.
 */
export type CatalogueRoom = {
  stockRoomId: string
  roomName: string
  roomCity: string
  /** on_hand - held in THIS room. Derived server-side (065), never here. */
  free: number
  canBackorder: boolean
}

export type CatalogueVariant = {
  variantId: string
  /** The rendered combination, from app.variant_label. Never rebuilt here. */
  label: string
  optionIds: string[]
  /** Already RESOLVED by app.shop_image_for — variant, else option, else item. */
  image: ShopImage | null
  /** on_hand - held, summed over the rooms asked for. Derived server-side (065). */
  free: number
  /**
   * 078. NO LONGER MADE, BUT STILL ON A SHELF YOU CAN REACH.
   *
   * Derived server-side from `not v.is_active`, and it only ever arrives true
   * on a combination that still has free stock — `shop_catalogue` drops a
   * retired variant the moment the last one goes, so there is no such thing
   * here as retired-and-empty. That is what makes "get it while it lasts"
   * truthful rather than decorative.
   *
   * Do NOT derive this from `free === 0`, and do not treat it as a reason to
   * grey the row out: a retired combination is fully orderable. What it cannot
   * do is be ordered IN — every `CatalogueRoom` under it has `canBackorder`
   * false, set server-side, so the existing room logic already does the right
   * thing without reading this flag at all.
   */
  isRetired: boolean
  /**
   * WHERE, per combination rather than per item — a navy medium in Lima and a
   * navy large in Burnaby are two different answers to "where do I go", and an
   * item-level list would send somebody to the wrong city.
   */
  rooms: CatalogueRoom[]
}

/**
 * Why this person cannot order this item, or null.
 *
 * One string from `app.shop_item_block_reason`, which is the SAME function the
 * order path calls (065's rule). The screen renders the reason and never
 * re-derives it: two answers to "may I have this" is a button that offers
 * something the server refuses.
 *
 * Note what is NOT in here: affordability. An item you cannot afford this month
 * is not locked — you are three recognitions away from it — so the price is
 * shown against your balance and the item stays open.
 */
export type BlockReason =
  | 'no_such_item' | 'inactive' | 'not_in_audience'
  | 'orders_not_open' | 'orders_closed' | 'max_reached'

export type CatalogueItem = {
  itemId: string
  code: string
  name: string
  description: string | null
  axes: ItemAxis[]
  priceStars: number
  isLocal: boolean
  ownerRoomId: string | null
  blockReason: BlockReason | null
  maxPerPerson: number | null
  myOrderCount: number
  ordersCloseAt: string | null
  /** 129: before this the item is listed and says when it opens. */
  ordersOpenAt: string | null
  /** 129: statements the buyer ticks before any order button works. */
  pledges: Pledge[]
  /** Null until a room is named — it is a property of the room and the item. */
  canBackorder: boolean | null
  /** The item default, for the card before any option is chosen. */
  image: ShopImage | null
  /**
   * TOTAL free stock for this item, across every active combination, in the
   * rooms asked for (073). What a tile says before anybody opens it.
   *
   * From `app.item_free` — NOT a sum of `variants[].free` computed here. 065's
   * rule is that free is derived in the database at every level it is shown, and
   * a TypeScript sum would be a second definition of availability whose first
   * disagreement would be about inactive variants.
   */
  free: number
  /**
   * The same number PER ROOM, and the same inclusion rule the variants use:
   * somewhere you could actually get it — free stock, or a room that will order
   * it in (069). Built server-side from `stock_room` left-joined to stock, so a
   * backorder room that has never held one is still listed.
   */
  rooms: CatalogueRoom[]
  variants: CatalogueVariant[]
}

export type OrderStatus = 'awaiting' | 'held' | 'collected' | 'cancelled'

export type MyOrder = {
  orderId: string
  itemId: string
  itemName: string
  /** SNAPSHOTTED at order time — what it says is what you bought (069). */
  variantLabel: string
  roomId: string
  roomName: string
  roomCity: string
  priceStars: number
  status: OrderStatus
  /** 1-based, and only while waiting. "You are third" is what makes it bearable. */
  queuePosition: number | null
  placedAt: string
  heldAt: string | null
  collectedAt: string | null
  cancelledAt: string | null
  mayCancel: boolean
  /**
   * REC-49. The buyer's half of the QR handover. `my_orders()` returns it ONLY
   * while there is something to hand over — held, unspent, unexpired — and
   * nulls it otherwise, so the card never invents one. Null means "no Pickup
   * with code button", nothing else.
   */
  handoverCode: string | null
  /** Snapshotted at issue (REC-12 rule 2). Shown so the buyer knows how long. */
  handoverExpiresAt: string | null
  /**
   * 129: the item's cutoff, if it has one. Lets a waiting order read as
   * "pre-ordered" rather than "37th in line" — the queue position is true and
   * useless when the supplier order is sized to the queue.
   */
  ordersCloseAt: string | null
}

export async function loadCatalogue(roomId: string | null): Promise<CatalogueItem[]> {
  const { data, error } = await supabase.rpc('shop_catalogue', { p_room: roomId })
  if (error) throw error
  return (data ?? []).map((r: Record<string, unknown>) => ({
    itemId: r.item_id as string,
    code: r.code as string,
    name: r.name as string,
    description: (r.description as string) ?? null,
    axes: readAxes(r.axes),
    priceStars: r.price_stars as number,
    isLocal: Boolean(r.is_local),
    ownerRoomId: (r.owner_room_id as string) ?? null,
    blockReason: (r.block_reason as BlockReason) ?? null,
    maxPerPerson: (r.max_per_person as number) ?? null,
    myOrderCount: (r.my_order_count as number) ?? 0,
    ordersCloseAt: (r.orders_close_at as string) ?? null,
    ordersOpenAt: (r.orders_open_at as string) ?? null,
    pledges: readPledges(r.pledges),
    canBackorder: r.can_backorder === null || r.can_backorder === undefined
      ? null
      : Boolean(r.can_backorder),
    image: readImage(r.image),
    free: (r.free as number) ?? 0,
    rooms: ((r.rooms ?? []) as Record<string, unknown>[]).map((rm) => ({
      stockRoomId: rm.stock_room_id as string,
      roomName: rm.room_name as string,
      roomCity: rm.room_city as string,
      free: (rm.free as number) ?? 0,
      canBackorder: Boolean(rm.can_backorder),
    })),
    variants: ((r.variants ?? []) as Record<string, unknown>[]).map((v) => ({
      variantId: v.variant_id as string,
      label: v.label as string,
      optionIds: ((v.option_ids ?? []) as string[]),
      image: readImage(v.image),
      free: (v.free as number) ?? 0,
      isRetired: Boolean(v.is_retired),
      rooms: ((v.rooms ?? []) as Record<string, unknown>[]).map((rm) => ({
        stockRoomId: rm.stock_room_id as string,
        roomName: rm.room_name as string,
        roomCity: rm.room_city as string,
        free: (rm.free as number) ?? 0,
        canBackorder: Boolean(rm.can_backorder),
      })),
    })),
  }))
}

export async function loadMyOrders(): Promise<MyOrder[]> {
  const { data, error } = await supabase.rpc('my_orders')
  if (error) throw error
  return (data ?? []).map((r: Record<string, unknown>) => ({
    orderId: r.order_id as string,
    itemId: r.item_id as string,
    itemName: r.item_name as string,
    variantLabel: r.variant_label as string,
    roomId: r.room_id as string,
    roomName: r.room_name as string,
    roomCity: r.room_city as string,
    priceStars: r.price_stars as number,
    status: r.status as OrderStatus,
    queuePosition: (r.queue_position as number) ?? null,
    placedAt: r.placed_at as string,
    heldAt: (r.held_at as string) ?? null,
    collectedAt: (r.collected_at as string) ?? null,
    cancelledAt: (r.cancelled_at as string) ?? null,
    mayCancel: Boolean(r.may_cancel),
    handoverCode: (r.handover_code as string) ?? null,
    handoverExpiresAt: (r.handover_expires_at as string) ?? null,
    ordersCloseAt: (r.orders_close_at as string) ?? null,
  }))
}

/**
 * `pledgeIds` must be EXACTLY the item's current pledges (129). The server
 * refuses a missing one and an id from wording that has since changed.
 */
export async function placeOrder(
  variantId: string,
  roomId: string,
  pledgeIds: string[] = [],
): Promise<string> {
  const { data, error } = await supabase.rpc('place_shop_order', {
    p_variant: variantId,
    p_room: roomId,
    p_pledges: pledgeIds,
  })
  if (error) throw error
  return data as string
}

export async function cancelOrder(orderId: string, reason?: string): Promise<void> {
  const { error } = await supabase.rpc('cancel_shop_order', {
    p_order: orderId,
    p_reason: reason ?? null,
  })
  if (error) throw error
  refreshWaiting() // 325: a count on the nav or the bell may have moved
}

/**
 * The sentence for a block reason.
 *
 * Written once, here, because the same reason appears on the card and in the
 * order path's error. 128 removed the category threshold, and with it the one
 * reason that needed numbers; if an endorsement-based unlock arrives, its
 * sentence says what to do next, not only that the door is shut.
 */
export function blockSentence(
  item: CatalogueItem,
  lang: HubLang = 'en',
): string {
  const t = SHOP[lang]
  switch (item.blockReason) {
    case 'orders_not_open':
      // Not "not available to you": it IS for them, from a date. Saying when
      // is the whole point of listing it before it opens (129).
      return item.ordersOpenAt
        ? t.opensOn(shortDate(item.ordersOpenAt, lang))
        : t.notOpenYet
    case 'orders_closed':
      return t.ordersClosed
    case 'max_reached':
      return item.maxPerPerson === 1
        ? t.oneEach
        : t.nEach(item.maxPerPerson ?? 0, item.myOrderCount ?? 0)
    case 'not_in_audience':
      return t.otherPart
    case 'inactive':
    case 'no_such_item':
      return t.noLonger
    default:
      return ''
  }
}

/** A date as the shop shows it: the reader's locale, no time. */
export function shortDate(iso: string, lang: HubLang = 'en'): string {
  return new Date(iso).toLocaleDateString(dateLocaleFor(lang), {
    year: 'numeric', month: 'short', day: 'numeric',
  })
}

/**
 * Whether to WORD this item as a pre-order. Presentation only, and derived
 * every time: an orders window closing plus somewhere that takes an order with
 * no stock. There is no stored flag (David, 14 Sep) — a third fact computed
 * from two would go stale the moment either moved.
 */
export function readsAsPreorder(item: CatalogueItem): boolean {
  return item.ordersCloseAt !== null && item.rooms.some((r) => r.canBackorder)
}

/**
 * What pressing the button will do to this variant, in this room, for this
 * person — or why there is no button.
 *
 * ONE function so the label and the enabled state cannot disagree. A card that
 * says "Order" and then refuses, or says "Out of stock" next to an enabled
 * button, is the failure 065's single block-reason function exists to prevent,
 * reappearing one layer up.
 */
export type OrderOffer =
  | { kind: 'order'; label: string }
  | { kind: 'backorder'; label: string }
  | { kind: 'none'; label: string; reason: string }

export function offerFor(
  item: CatalogueItem,
  variant: CatalogueVariant | null,
  /**
   * WHERE they would collect it. 072: the room used to be picked before the
   * catalogue rendered at all, which with one room auto-selected and with
   * sixteen left every shopper on an empty screen. It is a property of the
   * ORDER, so it is answered here, per combination.
   */
  room: CatalogueRoom | null,
  balanceAvailable: number,
  /** The label's language (SHL-25). The DECISION never reads it. */
  lang: HubLang = 'en',
): OrderOffer {
  const t = SHOP[lang]
  if (item.blockReason) {
    return { kind: 'none', label: t.notAvailable, reason: item.blockReason }
  }
  if (!variant) return { kind: 'none', label: t.chooseOption, reason: 'no_variant' }
  if (variant.rooms.length === 0) {
    // Nowhere holds it and nowhere will order it in. Distinct from "pick a
    // room": there is no room to pick.
    return { kind: 'none', label: t.notInAnyRoom, reason: 'out_of_stock' }
  }
  if (balanceAvailable < item.priceStars) {
    // Not a block reason (see BlockReason above) — a statement of fact about
    // today, phrased so it reads as "not yet" rather than "not for you".
    return {
      kind: 'none',
      label: t.moreNeeded((item.priceStars - balanceAvailable).toLocaleString()),
      reason: 'not_enough_stars',
    }
  }
  if (!room) return { kind: 'none', label: t.chooseWhere, reason: 'no_room' }
  // "Redeem from this store", not "Redeem" (David, 15 Sep). 073 moved the button
  // onto the per-ROOM row, where a bare verb would not say which shelf it meant
  // — and the row above it may be a different city.
  if (room.free > 0) return { kind: 'order', label: t.redeemHere }
  if (room.canBackorder) {
    return { kind: 'backorder', label: readsAsPreorder(item) ? t.preorderHere : t.joinWaitingList }
  }
  return { kind: 'none', label: t.noneThere, reason: 'out_of_stock' }
}

/** "Ready to collect", "3rd in line" — the one place an order's state is worded. */
export function orderState(o: MyOrder, lang: HubLang = 'en'): string {
  // The words — and English's ordinal suffixes — are lib/hubCopy.ts's SHOP.
  const t = SHOP[lang]
  switch (o.status) {
    case 'held':
      return t.readyToCollect
    case 'awaiting':
      // 129: a pre-order's queue is the supplier order, so a position in it is
      // noise. While the window is open or just shut, it reads as what it is.
      if (o.ordersCloseAt) return t.preordered
      return o.queuePosition === null
        ? t.waiting
        : o.queuePosition === 1
          ? t.nextInLine
          : t.inLine(o.queuePosition)
    case 'collected':
      return t.collected
    case 'cancelled':
      return t.cancelled
  }
}

/**
 * The zones a room may sit in. Not every IANA name — a list of six hundred is
 * a search box, and this one is short enough to read.
 *
 * SASKATCHEWAN IS THE ONE WORTH KNOWING, and until 071 this list got it
 * exactly backwards: it offered `America/Winnipeg` and not `America/Regina`.
 * Saskatchewan does NOT observe daylight saving, so a Zehner room on Winnipeg
 * is correct for half the year and an hour out for the other half — which is
 * the worst kind of wrong, because it works when you test it. Regina is in the
 * list now and Winnipeg stays for Manitoba, which is what it means.
 *
 * `America/Denver` was missing and Salt Lake City needs it. `Australia/Sydney`
 * and `America/Mexico_City` are here for the offices on the same contact page
 * that are not rooms yet — added now so that adding the room later is a form
 * submission rather than a release.
 *
 * UTC is last and is a real answer for nowhere. It survives only because the
 * column's default is UTC and a value the database can hold should be nameable
 * here; `create_stock_room` no longer defaults to it (071).
 */
export const ROOM_TIMEZONES = [
  'America/Vancouver',
  'America/Edmonton',
  'America/Denver',
  'America/Regina',
  'America/Winnipeg',
  'America/Toronto',
  'America/Los_Angeles',
  'America/Chicago',
  'America/New_York',
  'America/Mexico_City',
  'America/Lima',
  'America/Santiago',
  'America/Sao_Paulo',
  'Europe/London',
  'Europe/Paris',
  'Africa/Johannesburg',
  'Australia/Perth',
  'Australia/Brisbane',
  'Australia/Sydney',
  'UTC',
] as const

/* ══ REC-49 — THE QR HANDOVER, WEB HALF ════════════════════════════════════
 *
 * 077 built and proved the server side. These are the only three things the
 * browser needs from it, and two of them are the ONLY calls the signed-out
 * #/collect route makes: handover_lookup and handover_collect, both
 * anon-callable, both returning no ids at all (B21).
 */

/** The four states handover_lookup can say, plus not_ready. Never re-derived here (065). */
export type HandoverState = 'ready' | 'collected' | 'expired' | 'not_ready' | 'unknown'

export type HandoverView = {
  state: HandoverState
  itemName: string | null
  variantLabel: string | null
  roomName: string | null
  /** "Jamie S." — app.short_name, the B21 answer. Never a full name. */
  forWhom: string | null
  expiresAt: string | null
}

/**
 * 18 random bytes, hex — app.new_handover_code(). A string that is not that
 * shape is not a code we issued, so the page says "unknown" WITHOUT a round
 * trip: nothing typed into the address bar reaches the database as a probe.
 */
export function isHandoverCode(code: string): boolean {
  return /^[0-9a-f]{36}$/.test(code)
}

/**
 * The URL the QR encodes. THE ORIGIN IS WHEREVER THE CODE WAS SHOWN, never a
 * literal: a code made on the dev branch deploy must be scanned against the dev
 * database, where the order exists. A hard-coded oneconetec.com would send
 * every dev test to prod and read "unknown".
 *
 * A HASH, because every route here is (App.tsx). The fragment is also never
 * sent to a server, so a link previewer that fetches the URL learns nothing —
 * and it still could not spend it, because the page needs a press (Confirm).
 */
export function collectUrl(code: string, base?: string): string {
  const root = base ?? `${window.location.origin}${window.location.pathname}`
  return `${root}#/collect/${code}`
}

/** Groups of four, so it can be read out when a camera fails in a dark room. */
export function groupCode(code: string): string {
  return code.replace(/(.{4})(?=.)/g, '$1 ')
}

function readHandover(data: unknown): HandoverView {
  const r = (Array.isArray(data) ? data[0] : data) as Record<string, unknown> | undefined
  if (!r) return { state: 'unknown', itemName: null, variantLabel: null, roomName: null, forWhom: null, expiresAt: null }
  return {
    state: (r.state as HandoverState) ?? 'unknown',
    itemName: (r.item_name as string) ?? null,
    variantLabel: (r.variant_label as string) ?? null,
    roomName: (r.room_name as string) ?? null,
    forWhom: (r.for_whom as string) ?? null,
    expiresAt: (r.expires_at as string) ?? null,
  }
}

export async function lookupHandover(code: string): Promise<HandoverView> {
  const { data, error } = await supabase.rpc('handover_lookup', { p_code: code })
  if (error) throw error
  return readHandover(data)
}

/**
 * The Confirm press. Safe to call twice — the server's guarded UPDATE is both
 * the single-use and the race guard — and it returns the lookup AFTER the act,
 * so the screen re-reads rather than splices (028c).
 */
export async function collectHandover(code: string): Promise<HandoverView> {
  const { data, error } = await supabase.rpc('handover_collect', { p_code: code })
  if (error) throw error
  return readHandover(data)
}
